import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Avatar,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Chip,
  Fade,
  Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar,
  BatteryChargingFull
} from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faChargingStation,
  faIdCard
} from "@fortawesome/free-solid-svg-icons";
import { getVehiclesByUserAndPlace } from "../../services/vehicleService";
import { getPlacesByUserName } from "../../services/userService";
import CreateVehiculoModal from "./CreateVehiculoModal";
import EditVehiculoModal from "./EditVehiculoModal";
import DeleteVehiculoModal from "./DeleteVehiculoModal";

const MisVehiculosList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const fetchUserVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const placeId = sessionStorage.getItem("selectedPlaceId");
      const userDetailsResponse = await getPlacesByUserName();
      const userData = userDetailsResponse.data?.[0];

      if (userData?.id && placeId) {
        const params = {
          user_id: userData.id,
          place_id: parseInt(placeId, 10),
          page: currentPage + 1,
          page_size: rowsPerPage,
          sort_by: "created_at",
          sort_order: "desc"
        };
        const response = await getVehiclesByUserAndPlace(params);
        setVehicles(response.data || []);
        setTotalVehicles(response.total || 0);
      } else {
        setError("No se pudo obtener la información del usuario o la sede.");
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "No se pudieron cargar los vehículos.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage]);

  const handleCreateClick = () => setIsCreateModalOpen(true);

  const handleEditClick = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (vehiculo) => {
    setSelectedVehiculo(vehiculo);
    setIsDeleteModalOpen(true);
  };

  const handleCloseModal = (shouldRefresh = false) => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedVehiculo(null);
    if (shouldRefresh) fetchUserVehicles();
  };

  const handlePageChange = (_, newPage) => setCurrentPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const Content = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2
          }}
        >
          <CircularProgress size={60} sx={{ color: "#002642" }} />
          <Typography variant="body1" sx={{ color: "#75787B", textAlign: "center" }}>
            Cargando vehículos...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            border: "1px solid rgba(211, 47, 47, 0.2)",
            "& .MuiAlert-icon": { color: "#d32f2f" }
          }}
        >
          {error}
        </Alert>
      );
    }

    if (vehicles.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            py: 8
          }}
        >
          <Box
            sx={{
              p: 3,
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(0, 38, 66, 0.05) 0%, rgba(0, 87, 122, 0.05) 100%)",
              border: "2px dashed rgba(0, 38, 66, 0.1)"
            }}
          >
            <DirectionsCar sx={{ color: "#75787B", fontSize: "3rem" }} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ color: "#002642", fontWeight: 600, mb: 1 }}
            >
              No tienes vehículos registrados
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#75787B", maxWidth: 400, mx: "auto", mb: 3 }}
            >
              Registra tu primer vehículo eléctrico para comenzar a hacer reservas de carga.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              sx={{
                background: "linear-gradient(135deg, #002642 0%, #00577A 100%)",
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: "0 4px 16px rgba(0, 38, 66, 0.2)",
                "&:hover": {
                  boxShadow: "0 6px 24px rgba(0, 38, 66, 0.3)",
                  transform: "translateY(-1px)"
                }
              }}
            >
              Registrar Mi Primer Vehículo
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mb: { xs: 2, sm: 2.5, md: 3 }
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#002642",
                fontWeight: 600,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                mb: 0.5
              }}
            >
              Vehículos registrados ({totalVehicles})
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#75787B", fontSize: { xs: "0.85rem", sm: "0.95rem" } }}
            >
              Revisa muy bien el tipo de cargador del vehículo antes de registrarlo.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{
              background: "linear-gradient(135deg, #002642 0%, #00577A 100%)",
              borderRadius: 3,
              px: { xs: 3, sm: 4 },
              py: { xs: 1.5, sm: 1.75 },
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.9rem", sm: "1rem" },
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "180px" },
              boxShadow: "0 4px 16px rgba(0, 38, 66, 0.2)",
              "&:hover": {
                boxShadow: "0 6px 24px rgba(0, 38, 66, 0.3)",
                transform: "translateY(-1px)"
              }
            }}
          >
            {isMobile && !isTablet ? "Registrar" : "Registrar Vehículo"}
          </Button>
        </Box>

        {!isMobile && (
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              boxShadow: "0 2px 12px rgba(0, 38, 66, 0.08)",
              border: "1px solid rgba(0, 38, 66, 0.06)",
              overflow: "hidden",
              mb: { sm: 2, md: 3 }
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(0, 38, 66, 0.02)" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#002642",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                  >
                    Vehículo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#002642",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                  >
                    Modelo
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#002642",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                  >
                    Tipo de Cargador
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: "#002642",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px"
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.map((vehiculo) => (
                  <TableRow
                    key={vehiculo.vehicle_id}
                    sx={{
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "rgba(0, 38, 66, 0.02)",
                        transform: "scale(1.001)"
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          src={vehiculo.photo}
                          alt={`Foto de ${vehiculo.plate}`}
                          sx={{
                            width: 48,
                            height: 48,
                            border: "2px solid rgba(0, 38, 66, 0.1)"
                          }}
                        >
                          <DirectionsCar sx={{ color: "#75787B" }} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ color: "#002642", fontWeight: 600, fontSize: "0.95rem" }}
                          >
                            {vehiculo.plate}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#75787B", fontSize: "0.8rem" }}
                          >
                            <FontAwesomeIcon
                              icon={faIdCard}
                              style={{ marginRight: "6px", fontSize: "0.7rem" }}
                            />
                            Placa registrada
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#75787B", fontSize: "0.875rem" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FontAwesomeIcon
                          icon={faCar}
                          style={{ color: "#DB9600", fontSize: "0.8rem" }}
                        />
                        {vehiculo.model}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#75787B", fontSize: "0.875rem" }}>
                      <Chip
                        icon={<BatteryChargingFull sx={{ fontSize: "1rem" }} />}
                        label={vehiculo.charger_type}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(219, 150, 0, 0.1)",
                          color: "#DB9600",
                          fontWeight: 500,
                          "& .MuiChip-icon": { color: "#DB9600" }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Tooltip title="Editar vehículo" arrow>
                          <IconButton
                            onClick={() => handleEditClick(vehiculo)}
                            sx={{
                              color: "#002642",
                              backgroundColor: "rgba(0, 38, 66, 0.04)",
                              borderRadius: 2,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                backgroundColor: "#002642",
                                color: "white",
                                transform: "scale(1.05)"
                              }
                            }}
                          >
                            <EditIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar vehículo" arrow>
                          <IconButton
                            onClick={() => handleDeleteClick(vehiculo)}
                            sx={{
                              color: "#d32f2f",
                              backgroundColor: "rgba(211, 47, 47, 0.04)",
                              borderRadius: 2,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                backgroundColor: "#d32f2f",
                                color: "white",
                                transform: "scale(1.05)"
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {isMobile && (
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            {isTablet ? (
              <Grid container spacing={3}>
                {vehicles.map((vehiculo) => (
                  <Grid item xs={12} sm={6} key={vehiculo.vehicle_id}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        boxShadow: "0 2px 12px rgba(0, 38, 66, 0.08)",
                        border: "1px solid rgba(0, 38, 66, 0.06)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": {
                          boxShadow: "0 8px 32px rgba(0, 38, 66, 0.12)",
                          transform: "translateY(-2px)"
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2.5, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                          <Avatar
                            src={vehiculo.photo}
                            alt={`Foto de ${vehiculo.plate}`}
                            sx={{
                              width: 56,
                              height: 56,
                              border: "2px solid rgba(0, 38, 66, 0.1)"
                            }}
                          >
                            <DirectionsCar sx={{ color: "#75787B", fontSize: "1.8rem" }} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{ color: "#002642", fontWeight: 600, fontSize: "1.1rem", mb: 0.5 }}
                            >
                              {vehiculo.plate}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#75787B", fontSize: "0.9rem" }}>
                              {vehiculo.model}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: "rgba(0, 38, 66, 0.02)",
                            border: "1px solid rgba(0, 38, 66, 0.06)",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5
                          }}
                        >
                          <FontAwesomeIcon icon={faChargingStation} style={{ color: "#DB9600", fontSize: "1rem" }} />
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#75787B",
                                fontSize: "0.7rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                mb: 0.25
                              }}
                            >
                              Tipo de Cargador
                            </Typography>
                            <Typography variant="body1" sx={{ color: "#002642", fontWeight: 500, fontSize: "0.875rem" }}>
                              {vehiculo.charger_type}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>

                      <CardActions sx={{ p: 2.5, pt: 0, justifyContent: "flex-end", gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditClick(vehiculo)}
                          size="small"
                          sx={{
                            borderColor: "#002642",
                            color: "#002642",
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "0.8rem",
                            "&:hover": { backgroundColor: "#002642", color: "white", borderColor: "#002642" }
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(vehiculo)}
                          size="small"
                          sx={{
                            borderColor: "#d32f2f",
                            color: "#d32f2f",
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            fontSize: "0.8rem",
                            "&:hover": { backgroundColor: "#d32f2f", color: "white", borderColor: "#d32f2f" }
                          }}
                        >
                          Eliminar
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              vehicles.map((vehiculo) => (
                <Card
                  key={vehiculo.vehicle_id}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    boxShadow: "0 2px 12px rgba(0, 38, 66, 0.08)",
                    border: "1px solid rgba(0, 38, 66, 0.06)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    overflow: "hidden",
                    "&:hover": {
                      boxShadow: "0 8px 32px rgba(0, 38, 66, 0.12)",
                      transform: "translateY(-2px)"
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                      <Avatar
                        src={vehiculo.photo}
                        alt={`Foto de ${vehiculo.plate}`}
                        sx={{
                          width: 60,
                          height: 60,
                          border: "2px solid rgba(0, 38, 66, 0.1)"
                        }}
                      >
                        <DirectionsCar sx={{ color: "#75787B", fontSize: "1.8rem" }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{ color: "#002642", fontWeight: 600, fontSize: "1.1rem", mb: 0.5 }}
                        >
                          {vehiculo.plate}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#75787B", fontSize: "0.9rem" }}>
                          {vehiculo.model}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: "rgba(0, 38, 66, 0.02)",
                        border: "1px solid rgba(0, 38, 66, 0.06)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5
                      }}
                    >
                      <FontAwesomeIcon icon={faChargingStation} style={{ color: "#DB9600", fontSize: "1rem" }} />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#75787B",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            mb: 0.25
                          }}
                        >
                          Tipo de Cargador
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#002642", fontWeight: 500, fontSize: "0.875rem" }}>
                          {vehiculo.charger_type}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 2.5, pt: 0, justifyContent: "flex-end", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditClick(vehiculo)}
                      size="small"
                      sx={{
                        borderColor: "#002642",
                        color: "#002642",
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.8rem",
                        "&:hover": { backgroundColor: "#002642", color: "white", borderColor: "#002642" }
                      }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(vehiculo)}
                      size="small"
                      sx={{
                        borderColor: "#d32f2f",
                        color: "#d32f2f",
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.8rem",
                        "&:hover": { backgroundColor: "#d32f2f", color: "white", borderColor: "#d32f2f" }
                      }}
                    >
                      Eliminar
                    </Button>
                  </CardActions>
                </Card>
              ))
            )}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 2, sm: 2.5, md: 3 },
            "& .MuiTablePagination-root": {
              border: "1px solid rgba(0, 38, 66, 0.06)",
              borderRadius: 3,
              backgroundColor: "#FFFFFF",
              boxShadow: "0 2px 8px rgba(0, 38, 66, 0.04)",
              width: "100%",
              maxWidth: { xs: "100%", sm: "none" }
            }
          }}
        >
          <TablePagination
            component="div"
            count={totalVehicles}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 15]}
            labelRowsPerPage={isMobile ? "Por página:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              isMobile ? `${from}-${to} de ${count}` : `${from}-${to} de ${count} vehículos`
            }
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                color: "#002642",
                fontSize: { xs: "0.8rem", sm: "0.875rem" }
              },
              "& .MuiTablePagination-select": {
                color: "#002642",
                fontSize: { xs: "0.8rem", sm: "0.875rem" }
              },
              "& .MuiIconButton-root": {
                color: "#002642",
                padding: { xs: "8px", sm: "12px" },
                "&:hover": { backgroundColor: "rgba(0, 38, 66, 0.04)" }
              },
              "& .MuiTablePagination-toolbar": {
                minHeight: { xs: "52px", sm: "64px" },
                paddingLeft: { xs: "8px", sm: "16px" },
                paddingRight: { xs: "8px", sm: "16px" }
              }
            }}
          />
        </Box>
      </>
    );
  };

  return (
    <Fade in timeout={800}>
      <Box>
        <Content />
        <CreateVehiculoModal isOpen={isCreateModalOpen} onClose={handleCloseModal} />
        <EditVehiculoModal isOpen={isEditModalOpen} onClose={handleCloseModal} vehiculo={selectedVehiculo} />
        <DeleteVehiculoModal isOpen={isDeleteModalOpen} onClose={handleCloseModal} vehiculo={selectedVehiculo} />
      </Box>
    </Fade>
  );
};

export default MisVehiculosList;
